import type { Theme } from "../types/Theme";

export function resolveCardSortColumnGap(theme: Theme): number {
    return theme.CardSortColumnGap ?? 8;
}

export function resolveCardSortCardGap(theme: Theme): number {
    return theme.CardSortCardGap ?? 8;
}

export function resolveCardSortBucketPadding(theme: Theme): number {
    return theme.CardSortBucketPadding ?? 16;
}

export function resolveCardSortBucketTitlePaddingX(theme: Theme): number {
    return theme.CardSortBucketTitlePaddingX ?? 0;
}

export function resolveCardSortCardPadding(theme: Theme): number {
    return theme.CardSortCardPadding ?? 4;
}

/**
 * Modern Card Sort nests cards inside buckets, so each rounds a multiple of the field radius rather
 * than a fixed constant. The ratios reproduce the shipped modern look at the 8px default (card 12px,
 * bucket 16px) while still following Field Settings → Corners, and both square at "Squared".
 */
const MODERN_CARD_RADIUS_RATIO = 1.5;
const MODERN_BUCKET_RADIUS_RATIO = 2;

/** Shared ceiling so an extreme Field Corners value can't balloon nested Card Sort chrome. */
const CARD_SORT_MAX_RADIUS_PX = 50;

/** Card corner radius: 1.5× the field radius — 12px at the 8px default. */
export function resolveCardSortCardBorderRadius(theme: Theme): number {
    return Math.min(
        Math.round(theme.BorderRadius * MODERN_CARD_RADIUS_RATIO),
        CARD_SORT_MAX_RADIUS_PX,
    );
}

/** Bucket corner radius: 2× the field radius — 16px at the 8px default. */
export function resolveCardSortBucketBorderRadius(theme: Theme): number {
    return Math.min(
        Math.round(theme.BorderRadius * MODERN_BUCKET_RADIUS_RATIO),
        CARD_SORT_MAX_RADIUS_PX,
    );
}

export function resolveCardSortCardHeight(theme: Theme, fontSize?: number): number {
    const padding = resolveCardSortCardPadding(theme);
    const textSize =
        fontSize ??
        (parseInt(String(theme.TextFontSize ?? theme.BaseFontSize), 10) || theme.BaseFontSize);
    return Math.ceil(textSize * 1.25) + padding * 2;
}

export function resolveCardSortGroupDividerColor(theme: Theme): string {
    return theme.CardSortGroupDividerColor ?? theme.ControlBorderColor;
}

export function resolveCardSortDropZoneBorderColor(theme: Theme): string {
    return theme.CardSortDropZoneBorderColor ?? theme.ControlBorderColor;
}

export function resolveCardSortDragShadowColor(theme: Theme): string {
    return theme.CardSortDragShadowColor ?? "rgba(0,0,0,0.2)";
}

export function resolveCardSortDragLiftZ(theme: Theme): number {
    return theme.CardSortDragLiftZ ?? 10;
}

export function resolveCardSortGroupSectionGap(theme: Theme): number {
    return theme.CardSortGroupSectionGap ?? 16;
}

export function resolveCardSortCardHoverBoxShadow(theme: Theme): string {
    return theme.CardSortCardHoverBoxShadow ?? "none";
}
